import { useCallback, useEffect, useReducer, useRef } from 'react';

import type { MenuKey } from '../../../common/hooks/useMenuState';

type SectionMenuLevels = 1 | 2 | 3;

interface SectionStatePayload {
    level: SectionMenuLevels;
    id: MenuKey;
}

interface ActivateLevelAction {
    type: 'activateLevel';
    payload: SectionStatePayload;
}

interface ExpandLevelAction {
    type: 'expandLevel';
    payload: SectionStatePayload;
}

interface CollapseLevelAction {
    type: 'collapseLevel';
    payload: SectionMenuLevels;
}

interface CollapseAllLevelsAction {
    type: 'collapseAllLevels';
}

interface ExpandedSections extends Array<MenuKey | null> {}
interface ActiveSections extends Array<MenuKey | null> {}

interface SectionsMenuState {
    expanded: ExpandedSections;
    active: ActiveSections;
}
type SectionsMenuStateActions =
    | ExpandLevelAction
    | CollapseLevelAction
    | CollapseAllLevelsAction
    | ActivateLevelAction;

const reducer = (state: SectionsMenuState, action: SectionsMenuStateActions) => {
    switch (action.type) {
        case 'expandLevel': {
            if (action.payload.level === 1) {
                return {
                    ...state,
                    expanded: [action.payload.id],
                };
            }
            if (action.payload.level === 2) {
                return {
                    ...state,
                    expanded: [state.expanded[0], action.payload.id],
                };
            }
            if (action.payload.level === 3) {
                return {
                    ...state,
                    expanded: [state.expanded[0], state.expanded[1], action.payload.id],
                };
            }
            return { ...state };
        }
        case 'collapseLevel': {
            if (action.payload === 1) {
                return {
                    ...state,
                    expanded: [null, null, null],
                };
            }
            if (action.payload === 2) {
                return {
                    ...state,
                    expanded: [state.expanded[0], null, null],
                };
            }
            if (action.payload === 3) {
                return {
                    ...state,
                    expanded: [state.expanded[0], state.expanded[1], null],
                };
            }
            return { ...state };
        }
        case 'collapseAllLevels': {
            return {
                ...state,
                expanded: [null, null, null],
            };
        }
        case 'activateLevel': {
            if (action.payload.level === 1) {
                return {
                    ...state,
                    active: [action.payload.id],
                };
            }
            if (action.payload.level === 2) {
                return {
                    ...state,
                    active: [state.active[0], action.payload.id],
                };
            }
            if (action.payload.level === 3) {
                return {
                    ...state,
                    active: [state.active[0], state.active[1], action.payload.id],
                };
            }
            return { ...state };
        }
        default:
            throw new Error();
    }
};

const useSectionMenuState = () => {
    const [state, dispatch] = useReducer<SectionsMenuState, [SectionsMenuStateActions]>(reducer, {
        expanded: [null, null, null],
        active: [],
    });

    const activateLevel = useCallback((sectionId: MenuKey, level: SectionMenuLevels) => {
        dispatch({ type: 'activateLevel', payload: { id: sectionId, level } });
    }, []);

    const expandLevel = useCallback((sectionId: MenuKey, level: SectionMenuLevels) => {
        dispatch({ type: 'expandLevel', payload: { id: sectionId, level } });
    }, []);

    const collapseLevel = useCallback((level: SectionMenuLevels) => {
        dispatch({ type: 'collapseLevel', payload: level });
    }, []);

    const collapseAllLevels = useCallback(() => {
        dispatch({ type: 'collapseAllLevels' });
    }, []);

    const activeIdRef = useRef<MenuKey | null>(null);

    useEffect(() => {
        activeIdRef.current = state.active[state.active.length - 1];
    }, [state.active]);

    return {
        state,
        activateLevel,
        expandLevel,
        collapseLevel,
        collapseAllLevels,
        activeIdRef,
    };
};

export default useSectionMenuState;
